import type { EventCallback } from '@/services/pokerly-ws/callbacks.js';
import { io, Socket } from 'socket.io-client';
import { env } from '@/config/env.ts';

const SOCKET_OPERATION_TIMEOUT_MS = 10000; // 10 seconds

interface PokerlyWsOptions {
    token?: string; // Optional token for authentication (e.g., dev token)
    onConnect?: () => void; // Optional callback when connection is established
    onDisconnect?: () => void; // Optional callback when connection is lost
    onAny?: (event: string, payload: unknown) => void; // Optional callback for any event (for debugging)
}

function withTimeout<T>(promise: Promise<T>, timeout: number, operation: string): Promise<T> {
    return Promise.race([
        promise,
        new Promise<T>((_, reject) => {
            setTimeout(() => {
                reject(new Error(`${operation} timeout after ${timeout}ms`));
            }, timeout);
        }),
    ]);
}

export class PokerlyWsClient {
    public readonly socket: Socket;
    private _connectionStatus: 'idle' | 'established' = 'idle';
    private readonly listeners: Map<string, Set<EventCallback>> = new Map();

    constructor(options?: PokerlyWsOptions) {
        const auth = options?.token
            ? {
                auth: { token: options.token },
            }
            : { withCredentials: true };
        this.socket = io(env.VITE_API_ROOT, {
            autoConnect: true,
            reconnection: true,
            ...auth,
            transports: ['websocket'],
        });

        this.socket.on('connect', () => {
            options?.onConnect?.();
        });

        this.socket.on('disconnect', () => {
            options?.onDisconnect?.();
            this._connectionStatus = 'idle';
        });

        this.socket.onAny((event: string, payload: any) => {
            options?.onAny?.(event, payload);
        });
    }

    public get connectionStatus(): string {
        return this._connectionStatus;
    }

    public emitWithAck(
        event: string,
        payload: any,
        options?: { timeout?: number; description?: string },
    ): Promise<any> {
        const timeout = options?.timeout ?? SOCKET_OPERATION_TIMEOUT_MS;

        return withTimeout(
            this.socket.emitWithAck(event, payload),
            timeout,
            options?.description || `Emit event "${event}"`,
        );
    }

    public addListener(event: string, callback: EventCallback) {
        if (!this.listeners.has(event) || !this.listeners.get(event)) {
            this.listeners.set(event, new Set());
        }
        const listener = this.listeners.get(event);
        if (!listener) {
            throw new Error(`Listener set for event "${event}" was not properly initialized.`);
        }
        listener.add(callback);
    }

    public removeListener(event: string, callback: EventCallback) {
        const listenerSet = this.listeners.get(event);
        if (listenerSet) {
            listenerSet.delete(callback);
            if (listenerSet.size === 0) {
                this.listeners.delete(event);
            }
        }
    }

    public triggerToListeners(event: string, payload: any) {
        const listenerSet = this.listeners.get(event);
        if (listenerSet) {
            listenerSet.forEach((listener) => {
                try {
                    listener(payload);
                } catch (error) {
                    console.error(`Error in listener for event ${event}:`, error);
                }
            });
        }
    }

    connect() {
        if (this._connectionStatus === 'established') return;
        this._connectionStatus = 'established';
    }

    reconnect() {
        this._connectionStatus = 'idle';
        this.connect();
    }

    idle() {
        this._connectionStatus = 'idle';
    }

    disconnect() {
        this._connectionStatus = 'idle';
        this.listeners.clear();
        this.socket.disconnect();
    }
}
